import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDatepicker, UseDatepickerProps, MonthType } from "@datepicker-react/hooks";
import { addMonths, getMonth, getYear } from "date-fns";
import { usePathname } from "next/navigation";
import { NewDayObject } from "commonModule/calendar";
import { useAppSelector } from "stores/hooks";
import { selectUI } from "stores/ui/uiSlice";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { useUserAgentInfo } from "utils/hooks/useUserAgentInfo";
import CalendarLeftArrowIcon from "./arrows/CalendarLeftArrow";
import CalendarRightArrowIcon from "./arrows/CalendarRightArrow";
import DatepickerContext from "./datepicker-context";
import Month, { DayLabels } from "./month";
import NavButton from "./nav-button";
interface DatepickerProps extends UseDatepickerProps {
  activeMonths?: MonthType[];
  annotatedCalendarDates?: Record<string, NewDayObject>;
  changeActiveMonthOnSelect?: boolean;
  initialVisibleMonth?: Date;
  isAnnotated?: boolean;
  onClose?: () => void;
  onReset?: () => void;
  vertical?: boolean;
  isShowFlexibleDays?: boolean;
  afterCalendar?: ReactNode;
  beforeCalendar?: ({
    handleDateReset,
    onClose
  }: {
    handleDateReset?: () => void;
    onClose?: () => void;
  }) => ReactNode;
  isPaddingRemove?: boolean;
  isDisplayClearDates?: boolean;
}
const Datepicker = (props: DatepickerProps) => {
  // unbound issue coming from UseDatepickerProps so to prevent that this eslint rule disabled
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    startDate,
    endDate,
    minBookingDate,
    maxBookingDate,
    focusedInput,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    onDatesChange,
    initialVisibleMonth,
    vertical = false,
    onReset = () => {},
    annotatedCalendarDates,
    activeMonths: activeMonthsFromProp,
    isAnnotated = false,
    onClose,
    afterCalendar,
    beforeCalendar,
    isPaddingRemove = false,
    isDisplayClearDates = false
  } = props;
  const {
    activeMonths,
    focusedDate,
    isFirstOrLastSelectedDate,
    goToPreviousMonths,
    goToNextMonths,
    isDateHovered,
    isDateFocused,
    isDateSelected,
    onDateHover,
    onDateFocus,
    onDateSelect,
    onResetDates,
    isDateBlocked: isDateBlockedFn
  } = useDatepicker({
    startDate: startDate,
    endDate: endDate,
    focusedInput,
    onDatesChange,
    minBookingDate,
    maxBookingDate,
    initialVisibleMonth,
    numberOfMonths: 2,
    // default 2 months for desktop and mobile
    firstDayOfWeek: 0,
    changeActiveMonthOnSelect: false
  });
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const isListingPage = pathname?.startsWith("/vacation-rentals");
  const isFavoritesPage = pathname?.startsWith("/favorites");
  const isDetailsPage = pathname?.startsWith("/detail");
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const {
    isMobile
  } = useBreakpoint();
  const [isSmallerThan767] = useMediaQuery("(max-width: 767px)");
  const [numOfMonths, setNumOfMonths] = useState(2); // Initialize with 2 months

  const {
    isChromeOniOS,
    isIOSAndSafari
  } = useUserAgentInfo();
  const monthGridRef = useRef<HTMLDivElement>(null);
  const scrollTopToMonthGrid = useCallback(() => {
    if (monthGridRef && monthGridRef.current && vertical) {
      monthGridRef.current.scrollTop = 0;
    }
  }, [vertical]);
  const handleGoToNextMonth = useCallback(() => {
    goToNextMonths();
    scrollTopToMonthGrid();
  }, [goToNextMonths, scrollTopToMonthGrid]);
  const handleGoToPreviousMonth = useCallback(() => {
    goToPreviousMonths();
    scrollTopToMonthGrid();
  }, [goToPreviousMonths, scrollTopToMonthGrid]);
  const handleDateReset = () => {
    onResetDates();
    onReset();
  };
  // only trigger mobile screen & tablet screen size
  const [visibleMonths, setVisibleMonths] = useState(activeMonths);
  useEffect(() => {
    const initialMonths = Array.from({
      length: numOfMonths
    }, (_, i) => {
      const baseDate = initialVisibleMonth || new Date();
      return addMonths(baseDate, i);
    });
    // Map over the initialMonths array to format each date into an object with year, month, and a formatted date string.
    const formattedMonths = initialMonths.map(dateString => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth(); // getMonth() returns a 0-based index for the month (0 for January, 11 for December).

      return {
        year: year,
        month: month,
        date: new Date(year, month - 1, 1) // Keep date as string with the first day of the month
      };
    });
    setVisibleMonths(formattedMonths); // Update the visibleMonths state with the formatted array of month objects.
  }, [initialVisibleMonth, numOfMonths]);

  // Check if the last visible month is past the target date
  const lastVisibleMonth = visibleMonths[visibleMonths.length - 1];
  const isDisableLoadMore = lastVisibleMonth.month === getMonth(maxBookingDate || 0) && lastVisibleMonth.year === getYear(maxBookingDate || 0) || lastVisibleMonth.month >= getMonth(maxBookingDate || 0) && lastVisibleMonth.year >= getYear(maxBookingDate || 0);

  // reset numOfMonths when search tab change
  useEffect(() => {
    setNumOfMonths(2);
  }, [currentSearchTab]);
  const months = vertical ? activeMonthsFromProp || visibleMonths : activeMonths;
  const renderPrevButton = !vertical && <NavButton onClick={handleGoToPreviousMonth} icon={<CalendarLeftArrowIcon />} disabled={activeMonths[0].year > getYear(new Date()) ? false // can go back, if one already gone forward.
  : activeMonths[0].year < getYear(new Date()) ? true // can not go further back.
  : activeMonths[0].month <= getMonth(new Date())} />;
  const NextButton = !vertical && <NavButton onClick={handleGoToNextMonth} icon={<CalendarRightArrowIcon />} disabled={activeMonths[1].month === getMonth(maxBookingDate || 0) && activeMonths[1].year === getYear(maxBookingDate || 0) || activeMonths[1].month >= getMonth(maxBookingDate || 0) && activeMonths[1].year >= getYear(maxBookingDate || 0)} />;

  /** Effect: Automatically scroll to the selected month
   * This runs when:
   * - We're on a details page (`isDetailsPage`)
   * - The datepicker is vertical (mobile/tablet drawer)
   * - The months to display (`visibleMonths`) have been rendered
   * - A date is focused (`focusedDate`)
   * - We haven't already scrolled (`!hasScrolledToSelectedMonth`) */
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({}); // Ref to store references to each month DOM element

  // State to track if we've already auto-scrolled to the selected month
  // This prevents scrolling multiple times when the component re-renders
  const [hasScrolledToSelectedMonth, setHasScrolledToSelectedMonth] = useState(false);
  useEffect(() => {
    if (isDetailsPage && vertical && visibleMonths.length && focusedDate && !hasScrolledToSelectedMonth) {
      const selectedMonthKey = `${getYear(focusedDate)}-${getMonth(focusedDate)}`; // Build the key for the selected month (year-month)
      const monthElement = monthRefs.current[selectedMonthKey]; // Get the corresponding month DOM element from refs

      if (monthElement) {
        monthElement.scrollIntoView({
          behavior: "smooth",
          block: "center"
        }); // Scroll the month into view smoothly, centered in the viewport
      }
    }
  }, [isDetailsPage, vertical, visibleMonths, focusedDate, hasScrolledToSelectedMonth]);
  useEffect(() => {
    if (isDetailsPage && vertical && focusedDate) {
      // Calculate how many months we need to display
      // targetMonthIndex = number of months between initial visible month and selected month
      const targetMonthIndex = getMonth(focusedDate) - getMonth(initialVisibleMonth || new Date()) + 1;
      // If the selected month is not yet rendered, increase the number of months
      if (targetMonthIndex > visibleMonths.length) {
        setNumOfMonths(targetMonthIndex); // dynamically add more months to show
      }
    }
  }, [isDetailsPage, focusedDate, vertical, initialVisibleMonth]);
  return <DatepickerContext.Provider value={{
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    onDateFocus,
    focusedDate,
    onDateSelect,
    onDateHover,
    isDateBlocked: isDateBlockedFn,
    startDate: startDate,
    endDate: endDate
  }} data-sentry-element="DatepickerContext.Provider" data-sentry-component="Datepicker" data-sentry-source-file="datepicker.tsx">
      <div>
        <div className='relative min-h-[346px] w-full'>
          {beforeCalendar?.({
          onClose,
          handleDateReset
        })}
          {/* Rendering DayLabels component for mobile & tablet screen size */}
          <div className={`sticky left-0 right-0 top-0 z-[1] grid grid-cols-1 bg-white md:grid-cols-2 lg:hidden ${isFavoritesPage ? "px-4 shadow-[0px_5px_7px_0px_#37384d14] lg:px-0" : "border-b border-b-[#F7F6FC]"}`}>
            <DayLabels className={isFavoritesPage ? "sm:!grid-cols-[repeat(7,42px)]" : ""} data-sentry-element="DayLabels" data-sentry-source-file="datepicker.tsx" />
            <div className='hidden md:grid'>
              <DayLabels className={isFavoritesPage ? "sm:!grid-cols-[repeat(7,42px)]" : ""} data-sentry-element="DayLabels" data-sentry-source-file="datepicker.tsx" />
            </div>
          </div>
          <div className={`${isFavoritesPage ? "px-4 pt-3 lg:px-0 lg:pt-0" : ""} ${isPaddingRemove && isMobile ? "" : isMobile ? isChromeOniOS ? "pb-[200px]" : "pb-[250px]" : isChromeOniOS ? isListingPage ? "pb-[200px]" : "pb-[150px]" : isIOSAndSafari ? isListingPage ? "pb-[250px]" : "pb-[150px]" : ""}`}>
            <div className={`grid grid-cols-1 gap-2 md:grid-cols-2`} ref={monthGridRef}>
              {months.map((_month, idx) => <div key={idx} ref={(el: HTMLDivElement | null) => {
              monthRefs.current[`${_month.year}-${_month.month}`] = el;
            }}>
                  <Month key={`${_month.year}-${_month.month}`} year={_month.year} month={_month.month} annotatedCalendarDates={annotatedCalendarDates} prevButton={idx === 0 ? renderPrevButton : null} nextButton={vertical && isSmallerThan767 && idx === 0 || !vertical && idx === 1 || vertical && !isSmallerThan767 && idx === 1 ? NextButton : null} isAnnotated={isAnnotated} maxBookingDate={maxBookingDate} />
                </div>)}
            </div>
            {/* this 'Load more dates' button only show on mobile & tablet screen size */}
            {vertical && <div className='text-center'>
                <button disabled={isDisableLoadMore} aria-label='Load more dates' type='button' onClick={() => {
              setNumOfMonths(prev => prev + 2);
              setHasScrolledToSelectedMonth(true);
            }} className='my-5 rounded-full border border-[#BCC1D6] px-3 py-2 text-center text-xs font-medium leading-3 text-[#53535D]'>
                  Load more dates
                </button>
              </div>}
          </div>
        </div>
        {afterCalendar}
        {!vertical && isDisplayClearDates && !isPaymentPage && <button type='button' aria-label='Reset Dates' onClick={handleDateReset} className='btn btn-ghost text-base font-semibold text-secondary1000'>
            Clear dates
          </button>}
      </div>
    </DatepickerContext.Provider>;
};
export default Datepicker;